import { DataService } from '../services/data.js';
import { formatMoney, getIcon, showToast } from '../components/ui.js';
import { 
  parseCSV, 
  validateAndPrepareClientRows, 
  generateCSVTemplate, 
  exportClientsToCSV, 
  CSV_VALID_PHASES, 
  CSV_VALID_CHANNELS 
} from '../services/csvHelper.js';

let currentViewMode = 'kanban'; // 'kanban' | 'table'
let channelFilter = 'all'; // 'all' | 'Web Equipo Alerce' | 'Fiverr' | 'Directo'
let countryFilter = 'all';
let groupFilter = 'all';
let clientSearchTerm = '';
let cachedClients = [];
let cachedFilteredClients = [];
let pendingImportRows = [];
let cachedAccounts = [];
let cachedTemplates = [];
let cachedLogs = [];

function escapeHtml(str) {
  if (!str) return '';
  return String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

export async function renderPipelineView() {
  const [opportunities, clients, accounts, templates, logs] = await Promise.all([
    DataService.getOpportunities(),
    DataService.getClients(),
    DataService.getEmailAccounts(),
    DataService.getEmailTemplates(),
    DataService.getEmailLogs({ limit: 100 })
  ]);

  cachedClients = clients;
  cachedAccounts = accounts;
  cachedTemplates = templates;
  cachedLogs = logs;

  // Distinct countries & groups
  const countries = Array.from(new Set(clients.map(c => c.country).filter(Boolean)));
  const allGroups = Array.from(new Set(clients.flatMap(c => c.groups || []).filter(Boolean)));

  // Filter logic
  const term = clientSearchTerm.toLowerCase().trim();
  const filteredClients = clients.filter(c => {
    if (channelFilter !== 'all' && c.channel !== channelFilter) return false;
    if (countryFilter !== 'all' && c.country !== countryFilter) return false;
    if (groupFilter !== 'all' && !(c.groups || []).includes(groupFilter)) return false;
    if (term) {
      const match = 
        (c.name || '').toLowerCase().includes(term) ||
        (c.company || '').toLowerCase().includes(term) ||
        (c.country || '').toLowerCase().includes(term) ||
        (c.channel || '').toLowerCase().includes(term) ||
        (c.email || '').toLowerCase().includes(term) ||
        (c.phone || '').toLowerCase().includes(term) ||
        (c.socials || '').toLowerCase().includes(term) ||
        (c.notes || '').toLowerCase().includes(term) ||
        (c.contacts || []).some(cnt => (cnt.name || '').toLowerCase().includes(term) || (cnt.email || '').toLowerCase().includes(term));
      if (!match) return false;
    }
    return true;
  });

  cachedFilteredClients = filteredClients;

  const stages = [
    { id: 'Cliente potencial', label: 'Cliente Potencial' },
    { id: 'Propuesta en curso', label: 'Propuesta en Curso' },
    { id: 'Cerrado', label: 'Ganado / Activo' },
    { id: 'Descartado', label: 'Descartado' }
  ];

  return `
    <div class="space-y-6">
      <!-- Top Title & View Controls -->
      <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pb-3 border-b border-border">
        <div>
          <div class="flex items-center gap-2">
            <h1 class="text-2xl font-bold tracking-tight text-text-primary">Pipeline Comercial & Clientes</h1>
            <span class="text-xs font-mono px-2 py-0.5 rounded bg-surface-subtle border border-border">
              ${filteredClients.length === clients.length ? `${clients.length} Clientes / Leads` : `${filteredClients.length} de ${clients.length} Clientes`}
            </span>
          </div>
          <p class="text-xs text-text-secondary mt-0.5">Seguimiento comercial, gestión de contactos de empresa y reactivaciones</p>
        </div>

        <div class="flex flex-wrap items-center gap-2.5">
          <!-- View Toggle -->
          <div class="flex items-center p-1 bg-surface-subtle border border-border rounded-md">
            <button id="btn-view-kanban" class="px-2.5 py-1 text-xs font-semibold rounded ${
              currentViewMode === 'kanban' ? 'bg-white text-text-primary shadow-sm' : 'text-text-secondary hover:text-text-primary'
            }">
              Tablero Kanban
            </button>
            <button id="btn-view-table" class="px-2.5 py-1 text-xs font-semibold rounded ${
              currentViewMode === 'table' ? 'bg-white text-text-primary shadow-sm' : 'text-text-secondary hover:text-text-primary'
            }">
              Vista Lista
            </button>
          </div>

          <!-- CSV Export & Import Buttons -->
          <button id="btn-export-clients-csv" class="px-3 py-2 text-xs font-semibold rounded-md border border-border bg-surface hover:bg-surface-subtle transition-colors flex items-center gap-1.5 text-text-primary shadow-sm" title="Exportar clientes visibles con filtros a CSV">
            ${getIcon('download', 'w-3.5 h-3.5 text-text-secondary')}
            <span>Exportar CSV</span>
          </button>
          <button id="btn-open-import-modal" class="px-3 py-2 text-xs font-semibold rounded-md border border-border bg-surface hover:bg-surface-subtle transition-colors flex items-center gap-1.5 text-text-primary shadow-sm" title="Importar lista de prospectos y contactos desde CSV">
            ${getIcon('upload', 'w-3.5 h-3.5 text-text-secondary')}
            <span>Importar CSV</span>
          </button>

          <button id="btn-open-client-modal" class="px-3.5 py-2 text-xs font-semibold rounded-md bg-primary text-white hover:bg-primary-hover active:bg-primary-active transition-all shadow-sm flex items-center gap-2">
            ${getIcon('plus', 'w-3.5 h-3.5')}
            <span>Nuevo Cliente</span>
          </button>
        </div>
      </div>

      <!-- Filters & Search Toolbar -->
      <div class="flex flex-wrap items-center justify-between gap-3 p-3 bg-surface rounded-md border border-border">
        <!-- Search Input -->
        <div class="relative flex-1 min-w-[240px] max-w-md">
          <span class="absolute inset-y-0 left-0 pl-2.5 flex items-center pointer-events-none text-text-secondary">
            ${getIcon('search', 'w-3.5 h-3.5')}
          </span>
          <input 
            type="text" 
            id="input-search-clients" 
            placeholder="Buscar por cliente, contacto, empresa, email..." 
            value="${clientSearchTerm}" 
            class="w-full pl-8 pr-8 py-1.5 text-xs bg-surface-subtle border border-border rounded-md text-text-primary placeholder:text-text-secondary/60 focus:bg-surface focus:ring-2 focus:ring-primary outline-none transition-all"
          />
          ${clientSearchTerm ? `
            <button id="btn-clear-client-search" class="absolute inset-y-0 right-0 pr-2.5 flex items-center text-text-secondary hover:text-text-primary text-xs" title="Limpiar búsqueda">
              ✕
            </button>
          ` : ''}
        </div>

        <div class="flex flex-wrap items-center gap-3">
          <div class="flex flex-wrap items-center gap-2 text-xs">
            <span class="text-text-secondary font-semibold mr-1">Canal:</span>
            <button class="filter-channel-btn px-2.5 py-1 rounded text-xs font-medium border ${channelFilter === 'all' ? 'bg-primary text-white border-primary' : 'bg-surface text-text-secondary border-border'}" data-channel="all">Todos</button>
            <button class="filter-channel-btn px-2.5 py-1 rounded text-xs font-medium border ${channelFilter === 'Web Equipo Alerce' ? 'bg-primary text-white border-primary' : 'bg-surface text-text-secondary border-border'}" data-channel="Web Equipo Alerce">Web Alerce</button>
            <button class="filter-channel-btn px-2.5 py-1 rounded text-xs font-medium border ${channelFilter === 'Directo' ? 'bg-primary text-white border-primary' : 'bg-surface text-text-secondary border-border'}" data-channel="Directo">Directo</button>
            <button class="filter-channel-btn px-2.5 py-1 rounded text-xs font-medium border ${channelFilter === 'Fiverr' ? 'bg-primary text-white border-primary' : 'bg-surface text-text-secondary border-border'}" data-channel="Fiverr">Fiverr</button>
          </div>

          <div class="flex items-center gap-2 text-xs">
            <span class="text-text-secondary font-semibold">País:</span>
            <select id="select-country-filter" class="text-xs font-medium border border-border rounded px-2.5 py-1 bg-surface text-text-primary outline-none">
              <option value="all" ${countryFilter === 'all' ? 'selected' : ''}>Todos los países</option>
              ${countries.map(c => `<option value="${c}" ${countryFilter === c ? 'selected' : ''}>${c}</option>`).join('')}
            </select>
          </div>

          ${allGroups.length > 0 ? `
            <div class="flex items-center gap-2 text-xs">
              <span class="text-text-secondary font-semibold">Grupo:</span>
              <select id="select-group-filter" class="text-xs font-medium border border-border rounded px-2.5 py-1 bg-surface text-text-primary outline-none">
                <option value="all" ${groupFilter === 'all' ? 'selected' : ''}>Todos los grupos</option>
                ${allGroups.map(g => `<option value="${g}" ${groupFilter === g ? 'selected' : ''}>${g}</option>`).join('')}
              </select>
            </div>
          ` : ''}
        </div>
      </div>

      <!-- Main Display Area -->
      ${currentViewMode === 'kanban' ? `
        <!-- Kanban Board Grid -->
        <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          ${stages.map(stage => {
            const columnClients = filteredClients.filter(c => (c.phase || 'Cliente potencial') === stage.id);
            const totalProjects = columnClients.reduce((acc, c) => acc + (c.projects?.length || 0), 0);
            return `
              <div class="bg-surface-subtle/70 rounded-md border border-border p-4 flex flex-col min-h-[500px]">
                <!-- Column Header -->
                <div class="flex items-center justify-between pb-3 border-b border-border mb-3">
                  <div>
                    <h3 class="font-bold text-xs uppercase tracking-wider text-text-primary">${stage.label}</h3>
                    <p class="text-[10px] font-mono text-text-secondary mt-0.5">${columnClients.length} clientes • ${totalProjects} proyectos</p>
                  </div>
                  <button class="btn-quick-add-client text-text-secondary hover:text-text-primary p-1" data-stage="${stage.id}" title="Añadir cliente en esta etapa">
                    ${getIcon('plus', 'w-3.5 h-3.5')}
                  </button>
                </div>

                <!-- Card List -->
                <div class="space-y-2.5 flex-1">
                  ${columnClients.length === 0 ? `
                    <div class="h-32 border border-dashed border-border rounded flex items-center justify-center text-xs text-text-secondary/60">
                      Sin clientes en esta fase
                    </div>
                  ` : columnClients.map(c => {
                    const contactsCount = c.contacts?.length || 0;
                    const primaryContact = (c.contacts || []).find(cnt => cnt.is_primary) || (c.contacts || [])[0];
                    const lastContactStr = c.last_contacted_at ? new Date(c.last_contacted_at).toLocaleDateString('es-AR') : null;

                    return `
                      <div class="bg-surface p-3.5 rounded-md border border-border shadow-sm hover:border-zinc-400 transition-all space-y-2.5">
                        <div class="flex items-start justify-between gap-2">
                          <div>
                            <span class="font-bold text-xs text-text-primary leading-tight block">${c.name}</span>
                            ${primaryContact && primaryContact.name !== c.name ? `
                              <span class="text-[11px] text-text-secondary">Contacto: ${primaryContact.name}</span>
                            ` : ''}
                          </div>

                          <div class="flex items-center gap-1 shrink-0">
                            <select class="change-client-stage-select text-[10px] font-mono border border-border rounded px-1 py-0.5 bg-surface text-text-secondary outline-none" data-client-id="${c.id}">
                              ${stages.map(s => `<option value="${s.id}" ${(c.phase || 'Cliente potencial') === s.id ? 'selected' : ''}>${s.label}</option>`).join('')}
                            </select>
                          </div>
                        </div>

                        <div class="flex flex-wrap items-center gap-1 text-[10px]">
                          <span class="px-1.5 py-0.5 rounded font-mono font-medium ${
                            c.channel === 'Fiverr' 
                              ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' 
                              : (c.channel === 'Web Equipo Alerce'
                                ? 'bg-purple-50 text-purple-700 border border-purple-200 font-semibold'
                                : 'bg-surface-subtle text-text-secondary border border-border')
                          }">
                            ${c.channel || 'Directo'}
                          </span>
                          ${(c.groups || []).includes('diagnostico') || (c.notes || '').includes('[DIAGNÓSTICO WEB:') ? `
                            <span class="px-1.5 py-0.5 rounded font-mono font-bold bg-amber-50 text-amber-800 border border-amber-300">
                              🩺 Diagnóstico Web
                            </span>
                          ` : ''}
                          ${c.country ? `
                            <span class="px-1.5 py-0.5 rounded font-medium bg-surface-subtle border border-border text-text-secondary">
                              ${c.country}
                            </span>
                          ` : ''}
                          ${c.do_not_contact ? `
                            <span class="px-1.5 py-0.5 rounded font-medium bg-red-50 text-red-700 border border-red-200 font-mono">
                              No contactar
                            </span>
                          ` : ''}
                          ${contactsCount > 1 ? `
                            <span class="px-1.5 py-0.5 rounded font-medium bg-zinc-100 text-zinc-700 border border-zinc-200 font-mono">
                              ${contactsCount} contactos
                            </span>
                          ` : ''}
                        </div>

                        ${c.company && c.company !== '-' ? `
                          <p class="text-[11px] text-text-secondary font-medium truncate">
                            Empresa: <b>${c.company}</b>
                          </p>
                        ` : ''}

                        <!-- Last contact info -->
                        <div class="text-[10px] font-mono text-text-secondary">
                          Último contacto: ${lastContactStr ? `<b>${lastContactStr}</b>` : 'Sin registro'}
                        </div>

                        <!-- Card Action Buttons -->
                        <div class="pt-2 border-t border-border/60 flex items-center justify-between gap-1 text-xs">
                          <div class="flex items-center gap-1">
                            <button 
                              class="btn-reactivate-client px-2 py-1 text-[11px] font-medium rounded border border-border bg-surface hover:bg-surface-subtle text-text-primary flex items-center gap-1"
                              data-client-id="${c.id}"
                              title="Reactivar / Enviar Email directo sin crear campaña"
                            >
                              ${getIcon('mail', 'w-3 h-3 text-text-secondary')}
                              <span>Reactivar</span>
                            </button>
                            <button 
                              class="btn-record-reply px-2 py-1 text-[11px] font-medium rounded border border-border bg-surface hover:bg-emerald-50 hover:text-emerald-700 text-text-secondary flex items-center gap-1"
                              data-client-id="${c.id}"
                              title="Registrar respuesta recibida (detiene secuencias automáticas)"
                            >
                              ${getIcon('messageSquare', 'w-3 h-3')}
                              <span>Respuesta</span>
                            </button>
                          </div>

                          <button 
                            class="btn-edit-client p-1 text-text-secondary hover:text-text-primary rounded hover:bg-surface-subtle"
                            data-client-id="${c.id}"
                            title="Ver ficha completa y contactos"
                          >
                            ${getIcon('edit', 'w-3.5 h-3.5')}
                          </button>
                        </div>
                      </div>
                    `;
                  }).join('')}
                </div>
              </div>
            `;
          }).join('')}
        </div>
      ` : `
        <!-- Table View: Direct Client Directory -->
        <div class="bg-surface rounded-md border border-border shadow-card overflow-hidden">
          <div class="overflow-x-auto">
            <table class="w-full text-left text-xs">
              <thead class="bg-surface-subtle border-b border-border text-text-secondary uppercase font-semibold text-[10px]">
                <tr>
                  <th class="px-4 py-3">Cliente / Empresa</th>
                  <th class="px-4 py-3">Fase</th>
                  <th class="px-4 py-3">Canal / País</th>
                  <th class="px-4 py-3">Contactos</th>
                  <th class="px-4 py-3">Último Contacto</th>
                  <th class="px-4 py-3 text-right">Acciones Rápidas</th>
                </tr>
              </thead>
              <tbody class="divide-y divide-border/60 font-medium">
                ${filteredClients.map(c => {
                  const contactsList = c.contacts || [];
                  const lastContactStr = c.last_contacted_at ? new Date(c.last_contacted_at).toLocaleDateString('es-AR') : '-';
                  return `
                    <tr class="hover:bg-surface-subtle/50 transition-colors">
                      <td class="px-4 py-3">
                        <p class="font-bold text-text-primary">${c.name}</p>
                        <p class="text-[11px] text-text-secondary font-mono">${c.company && c.company !== '-' ? c.company : '-'}</p>
                      </td>
                      <td class="px-4 py-3">
                        <span class="px-2 py-0.5 rounded text-[10px] font-mono border ${
                          c.phase === 'Cerrado' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' :
                          c.phase === 'Descartado' ? 'bg-zinc-100 text-zinc-500 border-zinc-200' :
                          'bg-surface-subtle text-text-secondary border-border'
                        }">
                          ${c.phase === 'Cerrado' ? 'Ganado / Activo' : (c.phase || 'Cliente potencial')}
                        </span>
                      </td>
                      <td class="px-4 py-3 font-mono text-text-secondary text-[11px]">
                        <span>${c.channel || 'Directo'}</span>
                        ${(c.groups || []).includes('diagnostico') || (c.notes || '').includes('[DIAGNÓSTICO WEB:') ? `
                          <span class="ml-1 px-1.5 py-0.5 rounded text-[10px] font-bold bg-amber-50 text-amber-800 border border-amber-300">🩺 Diag</span>
                        ` : ''}
                        <span>• ${c.country || '-'}</span>
                      </td>
                      <td class="px-4 py-3 text-text-secondary text-[11px]">
                        ${contactsList.length > 0 ? `
                          <div class="space-y-0.5">
                            ${contactsList.slice(0, 2).map(cnt => `
                              <div class="truncate max-w-[200px] font-mono text-[11px]">
                                <span class="font-semibold text-text-primary">${cnt.name}</span>
                                ${cnt.email ? `(${cnt.email})` : ''}
                              </div>
                            `).join('')}
                            ${contactsList.length > 2 ? `<span class="text-[10px] text-text-secondary">+${contactsList.length - 2} más</span>` : ''}
                          </div>
                        ` : (c.email || '-')}
                      </td>
                      <td class="px-4 py-3 font-mono text-text-secondary text-[11px]">
                        ${lastContactStr}
                      </td>
                      <td class="px-4 py-3 text-right">
                        <div class="inline-flex items-center gap-1.5">
                          <button 
                            class="btn-reactivate-client px-2 py-1 text-xs font-semibold rounded border border-border bg-surface hover:bg-surface-subtle text-text-primary inline-flex items-center gap-1 shadow-sm"
                            data-client-id="${c.id}"
                            title="Reactivar / Enviar email rápido"
                          >
                            ${getIcon('mail', 'w-3 h-3 text-text-secondary')}
                            <span>Reactivar</span>
                          </button>
                          <button 
                            class="btn-record-reply px-2 py-1 text-xs font-semibold rounded border border-border bg-surface hover:bg-emerald-50 hover:text-emerald-700 text-text-secondary inline-flex items-center gap-1 shadow-sm"
                            data-client-id="${c.id}"
                            title="Registrar respuesta y detener secuencias"
                          >
                            ${getIcon('messageSquare', 'w-3 h-3')}
                          </button>
                          <button 
                            class="btn-edit-client px-2.5 py-1 text-xs font-semibold rounded border border-border bg-surface hover:bg-surface-subtle text-text-primary inline-flex items-center gap-1 shadow-sm"
                            data-client-id="${c.id}"
                            title="Ver ficha completa"
                          >
                            ${getIcon('edit', 'w-3 h-3')}
                            <span>Ficha</span>
                          </button>
                          <button 
                            class="btn-delete-client-quick p-1 text-text-secondary hover:text-red-600 hover:bg-red-50 rounded border border-border/60"
                            data-client-id="${c.id}"
                            data-client-name="${escapeHtml(c.name)}"
                            title="Eliminar lead"
                          >
                            ${getIcon('trash', 'w-3 h-3')}
                          </button>
                        </div>
                      </td>
                    </tr>
                  `;
                }).join('')}
              </tbody>
            </table>
          </div>
        </div>
      `}
    </div>

    <!-- Modal Editar / Ficha Completa del Cliente (con Múltiples Contactos e Historial de Emails) -->
    <div id="modal-client-new" class="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 hidden flex items-center justify-center p-4">
      <div class="bg-surface rounded-lg border border-border shadow-2xl max-w-2xl w-full max-h-[92vh] flex flex-col overflow-hidden animate-fade-in">
        <div class="p-4 border-b border-border flex items-center justify-between">
          <div>
            <h3 id="modal-client-title" class="font-bold text-sm text-text-primary">Ficha del Cliente</h3>
            <p class="text-[11px] text-text-secondary">Gestión de datos de empresa, contactos múltiples y supresión</p>
          </div>
          <button id="btn-close-client-modal" class="text-text-secondary hover:text-text-primary text-sm font-bold p-1">✕</button>
        </div>

        <div class="p-5 overflow-y-auto space-y-5 text-xs">
          <!-- Client form -->
          <form id="form-new-client" class="space-y-3.5">
            <input type="hidden" id="modal-client-id" value="" />

            <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label class="block font-semibold text-text-primary mb-1">Nombre Completo del Cliente</label>
                <input type="text" id="modal-client-name" placeholder="Ej: Juan Pérez" class="w-full text-xs border border-border rounded-md p-2 bg-surface text-text-primary outline-none" required />
              </div>
              <div>
                <label class="block font-semibold text-text-primary mb-1">Empresa / Marca</label>
                <input type="text" id="modal-client-company" placeholder="Ej: ABC S.A." class="w-full text-xs border border-border rounded-md p-2 bg-surface text-text-primary outline-none" />
              </div>
            </div>

            <div class="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <label class="block font-semibold text-text-primary mb-1">Fase Comercial</label>
                <select id="modal-client-phase" class="w-full text-xs border border-border rounded-md p-2 bg-surface text-text-primary outline-none">
                  <option value="Cliente potencial">Cliente potencial</option>
                  <option value="Propuesta en curso">Propuesta en curso</option>
                  <option value="Cerrado">Ganado / Activo</option>
                  <option value="Descartado">Descartado</option>
                </select>
              </div>
              <div>
                <label class="block font-semibold text-text-primary mb-1">Canal de Origen</label>
                <select id="modal-client-channel" class="w-full text-xs border border-border rounded-md p-2 bg-surface text-text-primary outline-none">
                  <option value="Directo">Directo</option>
                  <option value="Web Equipo Alerce">Web Equipo Alerce</option>
                  <option value="Fiverr">Fiverr</option>
                  <option value="Google Maps">Google Maps</option>
                  <option value="Google Search">Google Search</option>
                  <option value="LinkedIn">LinkedIn</option>
                  <option value="Referido">Referido</option>
                </select>
              </div>
              <div>
                <label class="block font-semibold text-text-primary mb-1">País</label>
                <input type="text" id="modal-client-country" placeholder="Argentina, Chile..." class="w-full text-xs border border-border rounded-md p-2 bg-surface text-text-primary outline-none" />
              </div>
            </div>

            <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label class="block font-semibold text-text-primary mb-1">Email Principal (referencia)</label>
                <input type="email" id="modal-client-email" placeholder="contacto@empresa.com" class="w-full text-xs border border-border rounded-md p-2 bg-surface text-text-primary outline-none font-mono" />
              </div>
              <div>
                <label class="block font-semibold text-text-primary mb-1">Teléfono / WhatsApp</label>
                <input type="text" id="modal-client-phone" placeholder="+54 9 11..." class="w-full text-xs border border-border rounded-md p-2 bg-surface text-text-primary outline-none font-mono" />
              </div>
            </div>

            <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label class="block font-semibold text-text-primary mb-1">Grupos / Categorías (separados por coma)</label>
                <input type="text" id="modal-client-groups" placeholder="Ej: PYME, E-commerce, B2B" class="w-full text-xs border border-border rounded-md p-2 bg-surface text-text-primary outline-none" />
              </div>
              <div>
                <label class="block font-semibold text-text-primary mb-1">Redes / Link Social</label>
                <input type="text" id="modal-client-socials" placeholder="LinkedIn, Instagram..." class="w-full text-xs border border-border rounded-md p-2 bg-surface text-text-primary outline-none font-mono" />
              </div>
            </div>

            <div>
              <label class="block font-semibold text-text-primary mb-1">Observaciones Comerciales</label>
              <textarea id="modal-client-notes" rows="2" placeholder="Notas sobre el cliente..." class="w-full text-xs border border-border rounded-md p-2 bg-surface text-text-primary outline-none"></textarea>
            </div>

            <!-- Suppression toggle -->
            <div class="p-2.5 bg-red-50/50 rounded border border-red-200/60 flex items-center justify-between">
              <div>
                <span class="font-bold text-red-800 text-xs">Lista de Supresión (No Contactar)</span>
                <p class="text-[11px] text-red-600">Si se activa, el sistema impedirá cualquier envío de campaña o email automatizado hacia esta empresa.</p>
              </div>
              <input type="checkbox" id="modal-client-dnc" class="rounded border-red-300 text-red-600 focus:ring-red-500 w-4 h-4" />
            </div>

            <div class="flex items-center justify-between gap-2 pt-2">
              <button type="button" id="btn-delete-client-modal" class="px-3 py-1.5 text-xs font-semibold rounded-md border border-red-200 bg-red-50 text-red-700 hover:bg-red-100 hover:text-red-800 transition-colors flex items-center gap-1.5 hidden" title="Eliminar este cliente y todos sus registros vinculados">
                ${getIcon('trash', 'w-3.5 h-3.5 text-red-600')}
                <span>Eliminar Lead</span>
              </button>
              <div class="flex items-center gap-2 ml-auto">
                <button type="button" id="btn-cancel-client" class="px-4 py-1.5 text-xs font-semibold rounded-md border border-border hover:bg-surface-subtle text-text-secondary">
                  Cancelar
                </button>
                <button type="submit" class="px-4 py-1.5 text-xs font-semibold rounded-md bg-primary text-white hover:bg-primary-hover active:bg-primary-active">
                  Guardar Cliente
                </button>
              </div>
            </div>
          </form>

          <!-- Contacts Sub-Manager (Only when editing existing client) -->
          <div id="client-contacts-section" class="pt-4 border-t border-border space-y-3">
            <div class="flex items-center justify-between">
              <div>
                <h4 class="font-bold text-xs uppercase tracking-wider text-text-primary">Contactos de la Empresa</h4>
                <p class="text-[11px] text-text-secondary">Permite tener múltiples interlocutores (Director, Compras, etc.) y definir destinatarios</p>
              </div>
              <button type="button" id="btn-toggle-add-contact" class="px-2.5 py-1 text-xs font-semibold rounded border border-border bg-surface-subtle hover:bg-surface text-text-primary flex items-center gap-1 shadow-sm">
                ${getIcon('plus', 'w-3 h-3')}
                <span>Agregar Contacto</span>
              </button>
            </div>

            <!-- Add/Edit contact inline form -->
            <form id="form-inline-add-contact" class="p-3 bg-surface-subtle rounded border border-border space-y-2.5 hidden">
              <input type="hidden" id="cnt-id" value="" />
              <div class="flex items-center justify-between border-b border-border/50 pb-1.5">
                <span id="cnt-form-title" class="font-bold text-xs text-text-primary">Nuevo Contacto</span>
                <span class="text-[10px] text-text-secondary" id="cnt-form-mode-hint">Completa los datos del interlocutor</span>
              </div>
              <div class="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <div>
                  <label class="block text-[11px] font-medium text-text-secondary mb-0.5">Nombre completo</label>
                  <input type="text" id="cnt-name" required placeholder="Ej: María Gómez" class="w-full text-xs p-1.5 bg-surface border border-border rounded outline-none" />
                </div>
                <div>
                  <label class="block text-[11px] font-medium text-text-secondary mb-0.5">Cargo / Rol</label>
                  <input type="text" id="cnt-role" placeholder="Ej: Directora de Operaciones" class="w-full text-xs p-1.5 bg-surface border border-border rounded outline-none" />
                </div>
              </div>
              <div class="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <div>
                  <label class="block text-[11px] font-medium text-text-secondary mb-0.5">Email</label>
                  <input type="email" id="cnt-email" placeholder="maria@empresa.com" class="w-full text-xs p-1.5 bg-surface border border-border rounded outline-none font-mono" />
                </div>
                <div>
                  <label class="block text-[11px] font-medium text-text-secondary mb-0.5">Teléfono / WhatsApp</label>
                  <input type="text" id="cnt-phone" placeholder="+54 9 11..." class="w-full text-xs p-1.5 bg-surface border border-border rounded outline-none font-mono" />
                </div>
              </div>
              <div class="flex justify-end gap-2 pt-1">
                <button type="button" id="btn-cancel-inline-contact" class="px-3 py-1 text-xs font-medium border border-border rounded text-text-secondary hover:bg-surface">Cancelar</button>
                <button type="submit" id="btn-submit-inline-contact" class="px-3 py-1 text-xs font-semibold rounded bg-primary text-white hover:bg-primary-hover shadow-sm">
                  <span id="cnt-submit-text">Guardar Contacto</span>
                </button>
              </div>
            </form>

            <!-- Contacts list -->
            <div id="client-contacts-list" class="space-y-1.5">
              <!-- Dynamically rendered -->
            </div>
          </div>

          <!-- Email History Sub-Section -->
          <div id="client-email-history-section" class="pt-4 border-t border-border space-y-2.5">
            <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1.5">
              <div>
                <h4 class="font-bold text-xs uppercase tracking-wider text-text-primary">Historial de Comunicaciones</h4>
                <p class="text-[11px] text-text-secondary">Envíos salientes, autor/remitente y respuestas obtenidas</p>
              </div>
              <div class="flex items-center gap-1.5">
                <label for="filter-history-sender" class="text-[10px] text-text-secondary font-medium">Remitente:</label>
                <select id="filter-history-sender" class="text-[11px] px-2 py-0.5 bg-surface-subtle border border-border rounded outline-none font-mono text-text-primary">
                  <option value="all">Todos los remitentes</option>
                </select>
              </div>
            </div>
            <div id="client-email-history-list" class="space-y-2 max-h-56 overflow-y-auto pr-1">
              <!-- Dynamically rendered -->
            </div>
          </div>

          <!-- Web Process Diagnostic Sub-Section (if client completed web diagnostic) -->
          <div id="client-diagnostic-section" class="pt-4 border-t border-border space-y-3 hidden">
            <div class="flex items-center justify-between">
              <div>
                <h4 class="font-bold text-xs uppercase tracking-wider text-text-primary flex items-center gap-1.5">
                  <span>🩺 Diagnóstico Inicial de Procesos</span>
                  <span id="diag-category-badge" class="px-2 py-0.5 rounded text-[10px] font-mono font-bold bg-purple-50 text-purple-700 border border-purple-200"></span>
                </h4>
                <p class="text-[11px] text-text-secondary" id="diag-submitted-info">Datos enviados desde el formulario web de Equipo Alerce</p>
              </div>
            </div>

            <div class="bg-surface-subtle p-3.5 rounded-md border border-border space-y-2.5">
              <div>
                <span class="text-[10px] uppercase font-bold text-text-secondary tracking-wider">Resumen del Diagnóstico:</span>
                <p id="diag-summary-text" class="text-xs text-text-primary mt-0.5 font-medium"></p>
              </div>
              <div>
                <span class="text-[10px] uppercase font-bold text-emerald-700 tracking-wider">Acción o Primer Paso Recomendado:</span>
                <p id="diag-action-text" class="text-xs text-emerald-900 mt-0.5 bg-emerald-50/80 p-2 rounded border border-emerald-200 font-medium"></p>
              </div>

              <!-- Collapsible original answers -->
              <details class="text-[11px] pt-1 border-t border-border/50">
                <summary class="cursor-pointer text-text-secondary hover:text-text-primary font-semibold select-none">
                  Ver respuestas originales del cuestionario (Q1 a Q4)
                </summary>
                <div id="diag-answers-container" class="mt-2.5 space-y-2 pl-3 border-l-2 border-primary/50 text-xs">
                  <!-- Dynamically rendered -->
                </div>
              </details>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal Importación Masiva de CSV de Contactos y Leads -->
    <div id="modal-import-clients" class="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 hidden flex items-center justify-center p-4">
      <div class="bg-surface rounded-lg border border-border shadow-2xl max-w-3xl w-full max-h-[92vh] flex flex-col overflow-hidden animate-fade-in">
        <div class="p-4 border-b border-border flex items-center justify-between">
          <div>
            <h3 class="font-bold text-sm text-text-primary flex items-center gap-2">
              ${getIcon('upload', 'w-4 h-4 text-primary')}
              <span>Importar Contactos y Leads desde CSV</span>
            </h3>
            <p class="text-[11px] text-text-secondary">Carga masiva hacia el Pipeline Comercial con validación previa de duplicados y formatos</p>
          </div>
          <button id="btn-close-import-modal" class="text-text-secondary hover:text-text-primary text-sm font-bold p-1">✕</button>
        </div>

        <div class="p-5 overflow-y-auto space-y-5 text-xs">
          <!-- Paso 1: Instrucciones y Plantilla de ejemplo -->
          <div class="p-3.5 bg-surface-subtle rounded-md border border-border space-y-3">
            <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
              <div>
                <h4 class="font-bold text-xs text-text-primary">Paso 1: Prepará tu archivo con el formato requerido</h4>
                <p class="text-[11px] text-text-secondary">Descargá la plantilla prearmada con las columnas compatibles y ejemplos de prueba</p>
              </div>
              <button 
                type="button" 
                id="btn-download-csv-template" 
                class="px-3 py-1.5 text-xs font-semibold rounded bg-surface border border-border hover:bg-surface-subtle text-text-primary shadow-sm flex items-center gap-1.5 shrink-0"
              >
                ${getIcon('download', 'w-3.5 h-3.5 text-primary')}
                <span>Descargar Plantilla CSV</span>
              </button>
            </div>

            <!-- Guía de columnas y restricciones -->
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-3 text-[11px] pt-1">
              <div class="space-y-1">
                <span class="font-bold text-text-primary block">Campos Aceptados:</span>
                <p>• <b>Nombre</b> <span class="text-rose-600 font-bold">(Obligatorio)</span>: Nombre del cliente/prospecto</p>
                <p>• <b>Empresa</b>: Marca, negocio o denominación</p>
                <p>• <b>Email</b>: Formato de correo válido</p>
                <p>• <b>Telefono</b>: Teléfono con código de área o internacional</p>
                <p>• <b>Pais</b>: País de radicación (ej: Argentina)</p>
              </div>
              <div class="space-y-1">
                <span class="font-bold text-text-primary block">Listas Desplegables y Opciones Válidas:</span>
                <p>• <b>Fase</b>: <code>Cliente potencial</code>, <code>Propuesta en curso</code>, <code>Cerrado</code>, <code>Descartado</code></p>
                <p>• <b>Canal</b>: <code>Directo</code>, <code>Web Equipo Alerce</code>, <code>Fiverr</code>, <code>Google Maps</code>, <code>LinkedIn</code></p>
                <p>• <b>Grupos</b>: Etiquetas separadas por coma (ej: <code>pyme, b2b</code>)</p>
                <p>• <b>No Contactar</b>: <code>si</code> o <code>no</code></p>
              </div>
            </div>
          </div>

          <!-- Paso 2: Selección de archivo -->
          <div class="space-y-2">
            <h4 class="font-bold text-xs text-text-primary">Paso 2: Seleccioná tu archivo CSV</h4>
            <div class="border-2 border-dashed border-border rounded-lg p-5 text-center hover:bg-surface-subtle/50 transition-colors cursor-pointer" id="drop-zone-csv">
              <input type="file" id="input-clients-csv-file" accept=".csv,text/csv" class="hidden" />
              <div class="space-y-1.5 flex flex-col items-center">
                ${getIcon('fileText', 'w-8 h-8 text-text-secondary')}
                <p class="text-xs font-semibold text-text-primary" id="csv-file-label">Haz clic aquí para seleccionar o arrastra tu archivo .CSV</p>
                <p class="text-[10px] text-text-secondary font-mono">Formatos admitidos: .csv codificado en UTF-8 o estándar</p>
              </div>
            </div>
          </div>

          <!-- Paso 3: Previsualización y Validación -->
          <div id="import-csv-preview-container" class="space-y-3 hidden">
            <div class="flex items-center justify-between">
              <h4 class="font-bold text-xs text-text-primary">Paso 3: Validación y Previsualización de Datos</h4>
              <span id="import-preview-count" class="text-xs font-mono font-bold text-text-secondary"></span>
            </div>

            <!-- KPI Strip de Validación -->
            <div class="grid grid-cols-3 gap-2.5 text-center">
              <div class="p-2 rounded bg-emerald-50 border border-emerald-200 text-emerald-800">
                <span class="text-[10px] uppercase font-bold block">Listos para Importar</span>
                <span id="badge-valid-count" class="text-base font-bold font-mono">0</span>
              </div>
              <div class="p-2 rounded bg-amber-50 border border-amber-200 text-amber-800">
                <span class="text-[10px] uppercase font-bold block">Duplicados a Omitir</span>
                <span id="badge-duplicate-count" class="text-base font-bold font-mono">0</span>
              </div>
              <div class="p-2 rounded bg-rose-50 border border-rose-200 text-rose-800">
                <span class="text-[10px] uppercase font-bold block">Con Errores</span>
                <span id="badge-error-count" class="text-base font-bold font-mono">0</span>
              </div>
            </div>

            <p class="text-[11px] text-text-secondary">
              ℹ️ Los registros detectados como duplicados (por email, teléfono o nombre existente) se <b>omitirán automáticamente</b> para preservar tus datos actuales sin sobreescrituras accidentales.
            </p>

            <!-- Tabla de Previsualización -->
            <div class="border border-border rounded-md overflow-x-auto max-h-56">
              <table class="w-full text-left text-[11px]">
                <thead class="bg-surface-subtle border-b border-border font-semibold text-text-secondary sticky top-0">
                  <tr>
                    <th class="px-3 py-2">Fila</th>
                    <th class="px-3 py-2">Estado</th>
                    <th class="px-3 py-2">Nombre</th>
                    <th class="px-3 py-2">Empresa</th>
                    <th class="px-3 py-2">Email</th>
                    <th class="px-3 py-2">Fase</th>
                    <th class="px-3 py-2">Canal</th>
                    <th class="px-3 py-2">Detalle / Motivo</th>
                  </tr>
                </thead>
                <tbody id="import-preview-tbody" class="divide-y divide-border/60 font-mono">
                  <!-- Dynamically rendered -->
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <!-- Footer -->
        <div class="p-4 border-t border-border bg-surface-subtle/50 flex items-center justify-between">
          <button type="button" id="btn-cancel-import-csv" class="px-4 py-1.5 text-xs font-semibold rounded-md border border-border hover:bg-surface text-text-secondary">
            Cancelar
          </button>
          <button 
            type="button" 
            id="btn-confirm-import-csv" 
            disabled 
            class="px-4 py-2 text-xs font-semibold rounded-md bg-primary text-white hover:bg-primary-hover active:bg-primary-active disabled:opacity-50 disabled:cursor-not-allowed shadow-sm flex items-center gap-2"
          >
            ${getIcon('check', 'w-3.5 h-3.5')}
            <span id="btn-confirm-import-text">Importar Contactos</span>
          </button>
        </div>
      </div>
    </div>

    <!-- Modals Express: Direct Reactivation & Manual Reply -->
    <div id="pipeline-extra-modals-container"></div>
  `;
}

export function initPipelineEvents() {
  // Search input
  const inputSearch = document.getElementById('input-search-clients');
  const btnClearSearch = document.getElementById('btn-clear-client-search');
  let searchDebounceTimer = null;

  if (inputSearch) {
    inputSearch.oninput = (e) => {
      clientSearchTerm = e.target.value;
      clearTimeout(searchDebounceTimer);
      searchDebounceTimer = setTimeout(() => {
        window.dispatchEvent(new HashChangeEvent('hashchange'));
        setTimeout(() => {
          const inp = document.getElementById('input-search-clients');
          if (inp) {
            inp.focus();
            inp.setSelectionRange(inp.value.length, inp.value.length);
          }
        }, 40);
      }, 180);
    };

    inputSearch.onkeydown = (e) => {
      if (e.key === 'Escape') {
        clientSearchTerm = '';
        window.dispatchEvent(new HashChangeEvent('hashchange'));
      }
    };
  }

  if (btnClearSearch) {
    btnClearSearch.onclick = () => {
      clientSearchTerm = '';
      window.dispatchEvent(new HashChangeEvent('hashchange'));
    };
  }

  // View mode toggles
  const btnKanban = document.getElementById('btn-view-kanban');
  const btnTable = document.getElementById('btn-view-table');

  if (btnKanban) btnKanban.onclick = () => { currentViewMode = 'kanban'; window.dispatchEvent(new HashChangeEvent('hashchange')); };
  if (btnTable) btnTable.onclick = () => { currentViewMode = 'table'; window.dispatchEvent(new HashChangeEvent('hashchange')); };

  // Filter channel buttons
  document.querySelectorAll('.filter-channel-btn').forEach(btn => {
    btn.onclick = () => {
      channelFilter = btn.dataset.channel;
      window.dispatchEvent(new HashChangeEvent('hashchange'));
    };
  });

  // Country select filter
  const countrySelect = document.getElementById('select-country-filter');
  if (countrySelect) {
    countrySelect.onchange = () => {
      countryFilter = countrySelect.value;
      window.dispatchEvent(new HashChangeEvent('hashchange'));
    };
  }

  // Group select filter
  const groupSelect = document.getElementById('select-group-filter');
  if (groupSelect) {
    groupSelect.onchange = () => {
      groupFilter = groupSelect.value;
      window.dispatchEvent(new HashChangeEvent('hashchange'));
    };
  }

  // Stage change select in Kanban cards
  document.querySelectorAll('.change-client-stage-select').forEach(sel => {
    sel.onchange = async () => {
      const clientId = sel.dataset.clientId;
      const newStage = sel.value;
      try {
        await DataService.updateClient(clientId, { phase: newStage });
        showToast(`Fase del cliente actualizada a "${newStage}"`);
        window.dispatchEvent(new HashChangeEvent('hashchange'));
      } catch (err) {
        showToast(`Error al cambiar fase: ${err.message}`, 'error');
      }
    };
  });

  // Direct Reactivate Buttons (Express Modal)
  document.querySelectorAll('.btn-reactivate-client').forEach(btn => {
    btn.onclick = (e) => {
      e.stopPropagation();
      openDirectReactivateModal(btn.dataset.clientId);
    };
  });

  // Record Reply Buttons (Express Modal)
  document.querySelectorAll('.btn-record-reply').forEach(btn => {
    btn.onclick = (e) => {
      e.stopPropagation();
      openRecordReplyModal(btn.dataset.clientId);
    };
  });

  // Client Modal handlers
  const modal = document.getElementById('modal-client-new');
  const form = document.getElementById('form-new-client');
  const btnOpen = document.getElementById('btn-open-client-modal');
  const btnClose = document.getElementById('btn-close-client-modal');
  const btnCancel = document.getElementById('btn-cancel-client');

  const btnDeleteClient = document.getElementById('btn-delete-client-modal');

  const openClientModalForNew = (defaultPhase = 'Cliente potencial') => {
    if (!modal) return;
    document.getElementById('modal-client-id').value = '';
    document.getElementById('modal-client-title').textContent = 'Registrar Nuevo Cliente / Lead';
    form.reset();
    document.getElementById('modal-client-phase').value = defaultPhase;
    document.getElementById('client-contacts-section')?.classList.add('hidden');
    document.getElementById('client-email-history-section')?.classList.add('hidden');
    document.getElementById('client-diagnostic-section')?.classList.add('hidden');
    if (btnDeleteClient) btnDeleteClient.classList.add('hidden');
    modal.classList.remove('hidden');
    document.body.classList.add('overflow-hidden');
  };

  const openClientModalForEdit = async (clientId) => {
    if (!modal) return;
    const client = cachedClients.find(c => String(c.id) === String(clientId));
    if (!client) {
      showToast('No se encontró el cliente', 'error');
      return;
    }

    document.getElementById('modal-client-id').value = client.id;
    document.getElementById('modal-client-title').textContent = `Ficha: ${client.name} (${client.company || 'Sin empresa'})`;
    document.getElementById('modal-client-name').value = client.name || '';
    document.getElementById('modal-client-channel').value = client.channel || 'Directo';
    document.getElementById('modal-client-country').value = client.country || '';
    document.getElementById('modal-client-company').value = client.company || '';
    document.getElementById('modal-client-phase').value = client.phase || 'Cliente potencial';
    document.getElementById('modal-client-email').value = client.email || '';
    document.getElementById('modal-client-phone').value = client.phone || '';
    document.getElementById('modal-client-socials').value = client.socials || '';
    document.getElementById('modal-client-notes').value = client.notes || '';
    document.getElementById('modal-client-groups').value = (client.groups || []).join(', ');
    document.getElementById('modal-client-dnc').checked = !!client.do_not_contact;

    // Show delete button in edit mode
    if (btnDeleteClient) {
      btnDeleteClient.classList.remove('hidden');
      btnDeleteClient.onclick = async () => {
        if (!confirm(`¿Estás seguro de eliminar al lead "${client.name}"?\nEsta acción eliminará sus contactos, oportunidades y registros vinculados.`)) return;
        try {
          btnDeleteClient.disabled = true;
          await DataService.deleteClient(client.id);
          showToast(`Cliente "${client.name}" eliminado correctamente`);
          modal.classList.add('hidden');
          document.body.classList.remove('overflow-hidden');
          window.dispatchEvent(new HashChangeEvent('hashchange'));
        } catch (err) {
          showToast(`Error al eliminar: ${err.message}`, 'error');
          btnDeleteClient.disabled = false;
        }
      };
    }

    // Show contacts & email history
    document.getElementById('client-contacts-section')?.classList.remove('hidden');
    document.getElementById('client-email-history-section')?.classList.remove('hidden');

    // Cargar Diagnóstico de Procesos si existe
    const diagSection = document.getElementById('client-diagnostic-section');
    if (diagSection) {
      try {
        const diag = await DataService.getLeadDiagnosticByClientId(client.id, client.notes);
        if (diag) {
          const catBadge = document.getElementById('diag-category-badge');
          if (catBadge) catBadge.textContent = diag.diagnosis_category || 'Diagnóstico';
          
          const sumText = document.getElementById('diag-summary-text');
          if (sumText) sumText.textContent = diag.diagnosis_summary || 'Sin resumen registrado';

          const actText = document.getElementById('diag-action-text');
          if (actText) actText.textContent = diag.recommended_action || 'Sin acción sugerida';

          const subInfo = document.getElementById('diag-submitted-info');
          if (subInfo && diag.submitted_at) {
            subInfo.textContent = `Enviado el ${new Date(diag.submitted_at).toLocaleString('es-AR')}`;
          }

          const answersCont = document.getElementById('diag-answers-container');
          if (answersCont) {
            answersCont.innerHTML = `
              <div><b class="text-text-secondary">Q1 - Situación Principal:</b> <p class="text-text-primary mt-0.5">${escapeHtml(diag.q1_problem || '-')}</p></div>
              <div><b class="text-text-secondary">Q2 - Cómo Resuelve Actualmente:</b> <p class="text-text-primary mt-0.5">${escapeHtml(diag.q2_current_method || '-')}</p></div>
              <div><b class="text-text-secondary">Q3 - Impacto en el Trabajo:</b> <p class="text-text-primary mt-0.5">${escapeHtml(diag.q3_impact || '-')}</p></div>
              <div><b class="text-text-secondary">Q4 - Qué Quiere Mejorar Primero:</b> <p class="text-text-primary mt-0.5">${escapeHtml(diag.q4_priority || '-')}</p></div>
            `;
          }

          diagSection.classList.remove('hidden');
        } else {
          diagSection.classList.add('hidden');
        }
      } catch (_) {
        diagSection.classList.add('hidden');
      }
    }

    // Reset inline contact form
    const inlineContactForm = document.getElementById('form-inline-add-contact');
    if (inlineContactForm) {
      inlineContactForm.reset();
      const cntIdInput = document.getElementById('cnt-id');
      if (cntIdInput) cntIdInput.value = '';
      const cntTitle = document.getElementById('cnt-form-title');
      if (cntTitle) cntTitle.textContent = 'Nuevo Contacto';
      const cntSubmit = document.getElementById('cnt-submit-text');
      if (cntSubmit) cntSubmit.textContent = 'Guardar Contacto';
      inlineContactForm.classList.add('hidden');
    }

    renderClientContactsList(client);
    renderClientEmailHistory(client);

    modal.classList.remove('hidden');
    document.body.classList.add('overflow-hidden');
  };

  if (btnOpen) btnOpen.onclick = () => openClientModalForNew();

  document.querySelectorAll('.btn-quick-add-client').forEach(btn => {
    btn.onclick = () => openClientModalForNew(btn.dataset.stage);
  });

  document.querySelectorAll('.btn-edit-client').forEach(btn => {
    btn.onclick = (e) => {
      e.stopPropagation();
      openClientModalForEdit(btn.dataset.clientId);
    };
  });

  document.querySelectorAll('.btn-delete-client-quick').forEach(btn => {
    btn.onclick = async (e) => {
      e.stopPropagation();
      const clientId = btn.dataset.clientId;
      const clientName = btn.dataset.clientName || 'este lead';
      if (!confirm(`¿Eliminar al lead "${clientName}"?\nSe borrarán sus contactos y registros relacionados del sistema.`)) return;
      try {
        await DataService.deleteClient(clientId);
        showToast(`Lead "${clientName}" eliminado`);
        window.dispatchEvent(new HashChangeEvent('hashchange'));
      } catch (err) {
        showToast(`Error al eliminar: ${err.message}`, 'error');
      }
    };
  });

  [btnClose, btnCancel].forEach(b => {
    if (b) {
      b.onclick = () => {
        modal.classList.add('hidden');
        document.body.classList.remove('overflow-hidden');
      };
    }
  });

  // --- Exportación de Clientes a CSV ---
  const btnExport = document.getElementById('btn-export-clients-csv');
  if (btnExport) {
    btnExport.onclick = () => {
      if (!cachedFilteredClients || cachedFilteredClients.length === 0) {
        showToast('No hay clientes o leads disponibles para exportar con los filtros actuales', 'error');
        return;
      }
      try {
        const csvData = exportClientsToCSV(cachedFilteredClients);
        const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        const now = new Date().toISOString().slice(0, 10);
        a.href = url;
        a.download = `alerce_contactos_pipeline_${now}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        showToast(`Se exportaron ${cachedFilteredClients.length} contactos a CSV correctamente`);
      } catch (err) {
        showToast(`Error al exportar CSV: ${err.message}`, 'error');
      }
    };
  }

  // --- Modal de Importación de CSV ---
  const modalImport = document.getElementById('modal-import-clients');
  const btnOpenImport = document.getElementById('btn-open-import-modal');
  const btnCloseImport = document.getElementById('btn-close-import-modal');
  const btnCancelImport = document.getElementById('btn-cancel-import-csv');
  const btnDownloadTemplate = document.getElementById('btn-download-csv-template');
  const inputCsvFile = document.getElementById('input-clients-csv-file');
  const dropZone = document.getElementById('drop-zone-csv');
  const previewContainer = document.getElementById('import-csv-preview-container');
  const previewTbody = document.getElementById('import-preview-tbody');
  const btnConfirmImport = document.getElementById('btn-confirm-import-csv');

  // Descarga de Plantilla CSV
  if (btnDownloadTemplate) {
    btnDownloadTemplate.onclick = () => {
      try {
        const templateCsv = generateCSVTemplate();
        const blob = new Blob([templateCsv], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'template_contactos_alerce.csv';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        showToast('Plantilla CSV descargada con éxito');
      } catch (err) {
        showToast(`Error al descargar plantilla: ${err.message}`, 'error');
      }
    };
  }

  // Apertura y cierre de modal de importación
  if (btnOpenImport && modalImport) {
    btnOpenImport.onclick = () => {
      pendingImportRows = [];
      if (inputCsvFile) inputCsvFile.value = '';
      if (previewContainer) previewContainer.classList.add('hidden');
      if (btnConfirmImport) {
        btnConfirmImport.disabled = true;
        const txt = document.getElementById('btn-confirm-import-text');
        if (txt) txt.textContent = 'Importar Contactos';
      }
      const label = document.getElementById('csv-file-label');
      if (label) label.textContent = 'Haz clic aquí para seleccionar o arrastra tu archivo .CSV';
      modalImport.classList.remove('hidden');
      document.body.classList.add('overflow-hidden');
    };
  }

  [btnCloseImport, btnCancelImport].forEach(b => {
    if (b && modalImport) {
      b.onclick = () => {
        modalImport.classList.add('hidden');
        document.body.classList.remove('overflow-hidden');
      };
    }
  });

  // Drag and drop & file select
  const handleCsvFileSelected = (file) => {
    if (!file || !file.name.toLowerCase().endsWith('.csv')) {
      showToast('Por favor selecciona un archivo con extensión .csv', 'error');
      return;
    }
    const label = document.getElementById('csv-file-label');
    if (label) label.textContent = `Archivo seleccionado: ${file.name} (${Math.round(file.size / 1024)} KB)`;

    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const text = ev.target.result;
        const parsedRecords = parseCSV(text);
        if (parsedRecords.length === 0) {
          showToast('El archivo CSV está vacío o no contiene registros válidos', 'error');
          return;
        }

        const evaluatedRows = validateAndPrepareClientRows(parsedRecords, cachedClients);
        pendingImportRows = evaluatedRows;

        const validCount = evaluatedRows.filter(r => r.status === 'valid').length;
        const dupCount = evaluatedRows.filter(r => r.status === 'duplicate').length;
        const errCount = evaluatedRows.filter(r => r.status === 'error').length;

        const elValid = document.getElementById('badge-valid-count');
        const elDup = document.getElementById('badge-duplicate-count');
        const elErr = document.getElementById('badge-error-count');
        const elCount = document.getElementById('import-preview-count');

        if (elValid) elValid.textContent = validCount;
        if (elDup) elDup.textContent = dupCount;
        if (elErr) elErr.textContent = errCount;
        if (elCount) elCount.textContent = `${evaluatedRows.length} filas analizadas`;

        if (previewTbody) {
          previewTbody.innerHTML = evaluatedRows.map(r => {
            const statusBadge = r.status === 'valid'
              ? `<span class="px-1.5 py-0.5 rounded font-bold bg-emerald-100 text-emerald-800 text-[10px]">Válido</span>`
              : (r.status === 'duplicate'
                ? `<span class="px-1.5 py-0.5 rounded font-bold bg-amber-100 text-amber-800 text-[10px]">Omitir</span>`
                : `<span class="px-1.5 py-0.5 rounded font-bold bg-rose-100 text-rose-800 text-[10px]">Inválido</span>`);

            const detailMsg = r.status === 'valid'
              ? `<span class="text-emerald-700">Listo para crear</span>`
              : (r.status === 'duplicate'
                ? `<span class="text-amber-700">${escapeHtml(r.duplicateReason)}</span>`
                : `<span class="text-rose-700 font-semibold">${escapeHtml(r.errors.join('; '))}</span>`);

            return `
              <tr class="${r.status === 'valid' ? 'hover:bg-emerald-50/30' : (r.status === 'duplicate' ? 'bg-amber-50/20' : 'bg-rose-50/30')}">
                <td class="px-3 py-1.5 text-text-secondary">${r._rowNumber}</td>
                <td class="px-3 py-1.5">${statusBadge}</td>
                <td class="px-3 py-1.5 font-bold text-text-primary truncate max-w-[140px]">${escapeHtml(r.clientData.name || '-')}</td>
                <td class="px-3 py-1.5 text-text-secondary truncate max-w-[120px]">${escapeHtml(r.clientData.company || '-')}</td>
                <td class="px-3 py-1.5 text-text-secondary truncate max-w-[140px]">${escapeHtml(r.clientData.email || '-')}</td>
                <td class="px-3 py-1.5 text-text-secondary">${escapeHtml(r.clientData.phase || '-')}</td>
                <td class="px-3 py-1.5 text-text-secondary">${escapeHtml(r.clientData.channel || '-')}</td>
                <td class="px-3 py-1.5 text-[10px]">${detailMsg}</td>
              </tr>
            `;
          }).join('');
        }

        if (previewContainer) previewContainer.classList.remove('hidden');

        if (btnConfirmImport) {
          btnConfirmImport.disabled = validCount === 0;
          const txt = document.getElementById('btn-confirm-import-text');
          if (txt) {
            txt.textContent = validCount > 0 
              ? `Importar ${validCount} ${validCount === 1 ? 'Contacto' : 'Contactos'}`
              : 'Sin filas válidas para importar';
          }
        }
      } catch (err) {
        showToast(`Error al procesar CSV: ${err.message}`, 'error');
      }
    };
    reader.readAsText(file);
  };

  if (dropZone && inputCsvFile) {
    dropZone.onclick = () => inputCsvFile.click();
    dropZone.ondragover = (e) => { e.preventDefault(); dropZone.classList.add('bg-surface-subtle'); };
    dropZone.ondragleave = () => { dropZone.classList.remove('bg-surface-subtle'); };
    dropZone.ondrop = (e) => {
      e.preventDefault();
      dropZone.classList.remove('bg-surface-subtle');
      if (e.dataTransfer?.files?.length > 0) {
        handleCsvFileSelected(e.dataTransfer.files[0]);
      }
    };
  }

  if (inputCsvFile) {
    inputCsvFile.onchange = (e) => {
      if (e.target.files?.length > 0) {
        handleCsvFileSelected(e.target.files[0]);
      }
    };
  }

  // Confirmar Importación
  if (btnConfirmImport) {
    btnConfirmImport.onclick = async () => {
      const validRows = pendingImportRows.filter(r => r.status === 'valid');
      if (validRows.length === 0) {
        showToast('No hay registros válidos para importar', 'error');
        return;
      }

      btnConfirmImport.disabled = true;
      const textSpan = document.getElementById('btn-confirm-import-text');
      if (textSpan) textSpan.textContent = 'Importando contactos...';

      try {
        const res = await DataService.importClientsBatch(validRows);
        const dupCount = pendingImportRows.filter(r => r.status === 'duplicate').length;
        
        let msg = `Se importaron ${res.imported} contactos correctamente.`;
        if (dupCount > 0) msg += ` (${dupCount} duplicados omitidos).`;
        showToast(msg);

        if (modalImport) modalImport.classList.add('hidden');
        document.body.classList.remove('overflow-hidden');
        window.dispatchEvent(new HashChangeEvent('hashchange'));
      } catch (err) {
        showToast(`Error durante la importación: ${err.message}`, 'error');
        if (textSpan) textSpan.textContent = 'Reintentar Importación';
        btnConfirmImport.disabled = false;
      }
    };
  }

  if (form) {
    form.onsubmit = async (e) => {
      e.preventDefault();
      const clientId = document.getElementById('modal-client-id').value;
      const groupsRaw = document.getElementById('modal-client-groups').value;
      const groups = groupsRaw.split(',').map(g => g.trim()).filter(Boolean);

      const clientData = {
        name: document.getElementById('modal-client-name').value,
        channel: document.getElementById('modal-client-channel').value,
        country: document.getElementById('modal-client-country').value,
        company: document.getElementById('modal-client-company').value,
        phase: document.getElementById('modal-client-phase').value,
        email: document.getElementById('modal-client-email').value,
        phone: document.getElementById('modal-client-phone').value,
        socials: document.getElementById('modal-client-socials').value,
        notes: document.getElementById('modal-client-notes').value,
        groups: groups,
        do_not_contact: document.getElementById('modal-client-dnc').checked
      };

      try {
        if (clientId) {
          await DataService.updateClient(clientId, clientData);
          showToast('Cliente actualizado correctamente');
        } else {
          const client = await DataService.createClient(clientData);

          // Create primary contact automatically
          await DataService.createContact({
            client_id: client.id,
            name: client.name,
            email: client.email,
            phone: client.phone,
            role: 'Contacto Principal',
            is_primary: true
          });

          await DataService.createOpportunity({
            client_id: client.id,
            title: `Proyecto inicial - ${client.name}`,
            stage: client.phase,
            estimated_amount: 0,
            currency: 'ARS',
            notes: client.notes
          });

          showToast('Cliente, contacto y oportunidad creados con éxito');
        }

        modal.classList.add('hidden');
        document.body.classList.remove('overflow-hidden');
        window.dispatchEvent(new HashChangeEvent('hashchange'));
      } catch (err) {
        showToast(`Error al guardar cliente: ${err.message}`, 'error');
      }
    };
  }

  // Add/Edit contact inline form logic
  const btnToggleAddContact = document.getElementById('btn-toggle-add-contact');
  const inlineContactForm = document.getElementById('form-inline-add-contact');
  const btnCancelInline = document.getElementById('btn-cancel-inline-contact');

  if (btnToggleAddContact && inlineContactForm) {
    btnToggleAddContact.onclick = () => {
      const cntIdInput = document.getElementById('cnt-id');
      const isCurrentlyEditing = cntIdInput && cntIdInput.value !== '';
      
      // If form is visible and in edit mode, switch back to clean create mode
      if (!inlineContactForm.classList.contains('hidden') && isCurrentlyEditing) {
        inlineContactForm.reset();
        if (cntIdInput) cntIdInput.value = '';
        document.getElementById('cnt-form-title').textContent = 'Nuevo Contacto';
        document.getElementById('cnt-submit-text').textContent = 'Guardar Contacto';
        document.getElementById('cnt-name')?.focus();
        return;
      }

      // If form is hidden, open in create mode
      if (inlineContactForm.classList.contains('hidden')) {
        inlineContactForm.reset();
        if (cntIdInput) cntIdInput.value = '';
        document.getElementById('cnt-form-title').textContent = 'Nuevo Contacto';
        document.getElementById('cnt-submit-text').textContent = 'Guardar Contacto';
        inlineContactForm.classList.remove('hidden');
        document.getElementById('cnt-name')?.focus();
      } else {
        inlineContactForm.classList.add('hidden');
      }
    };

    btnCancelInline.onclick = () => {
      inlineContactForm.reset();
      const cntIdInput = document.getElementById('cnt-id');
      if (cntIdInput) cntIdInput.value = '';
      document.getElementById('cnt-form-title').textContent = 'Nuevo Contacto';
      document.getElementById('cnt-submit-text').textContent = 'Guardar Contacto';
      inlineContactForm.classList.add('hidden');
    };

    inlineContactForm.onsubmit = async (e) => {
      e.preventDefault();
      const clientId = document.getElementById('modal-client-id').value;
      if (!clientId) return;

      const contactId = document.getElementById('cnt-id')?.value;
      const name = document.getElementById('cnt-name').value.trim();
      const role = document.getElementById('cnt-role').value.trim() || 'Contacto';
      const email = document.getElementById('cnt-email').value.trim();
      const phone = document.getElementById('cnt-phone').value.trim();

      try {
        if (contactId) {
          await DataService.updateContact(Number(contactId), {
            name,
            role,
            email,
            phone
          });
          showToast('Contacto actualizado correctamente.');
        } else {
          const currentClient = cachedClients.find(c => String(c.id) === String(clientId));
          const isFirstContact = !currentClient || (currentClient.contacts || []).length === 0;

          await DataService.createContact({
            client_id: Number(clientId),
            name,
            role,
            email,
            phone,
            is_primary: isFirstContact,
            do_not_contact: false
          });
          showToast('Contacto agregado correctamente.');
        }

        inlineContactForm.reset();
        const cntIdInput = document.getElementById('cnt-id');
        if (cntIdInput) cntIdInput.value = '';
        document.getElementById('cnt-form-title').textContent = 'Nuevo Contacto';
        document.getElementById('cnt-submit-text').textContent = 'Guardar Contacto';
        inlineContactForm.classList.add('hidden');
        
        // Refresh local cache and list
        const updatedClients = await DataService.getClients();
        cachedClients = updatedClients;
        const currentClient = updatedClients.find(c => String(c.id) === String(clientId));
        if (currentClient) renderClientContactsList(currentClient);
      } catch (err) {
        showToast(`Error: ${err.message}`, 'error');
      }
    };
  }

  // Auto-apertura de ficha de lead si la URL contiene ?clientId=...
  const currentHash = window.location.hash || '';
  const queryIndex = currentHash.indexOf('?');
  if (queryIndex !== -1) {
    const params = new URLSearchParams(currentHash.substring(queryIndex + 1));
    const targetClientId = params.get('clientId');
    if (targetClientId) {
      setTimeout(() => {
        openClientModalForEdit(targetClientId);
      }, 120);
    }
  }
}

// -------------------------------------------------------------
// RENDER CLIENT CONTACTS IN MODAL
// -------------------------------------------------------------
function renderClientContactsList(client) {
  const container = document.getElementById('client-contacts-list');
  if (!container) return;

  const contacts = client.contacts || [];
  if (contacts.length === 0) {
    container.innerHTML = `<p class="text-text-secondary italic p-2 bg-surface-subtle rounded text-center">No hay contactos registrados para este cliente.</p>`;
    return;
  }

  container.innerHTML = contacts.map(cnt => `
    <div class="p-2.5 bg-surface-subtle rounded border border-border/70 flex items-center justify-between gap-2">
      <div class="min-w-0 flex-1">
        <div class="flex items-center gap-2 flex-wrap">
          <span class="font-bold text-text-primary text-xs truncate">${cnt.name}</span>
          ${cnt.is_primary ? `
            <span class="text-[9px] font-mono px-1.5 py-0.2 rounded bg-emerald-50 text-emerald-700 border border-emerald-200 font-semibold">
              Principal
            </span>
          ` : ''}
          ${cnt.do_not_contact ? `
            <span class="text-[9px] font-mono px-1.5 py-0.2 rounded bg-red-50 text-red-700 border border-red-200 font-semibold">
              No contactar
            </span>
          ` : ''}
        </div>
        <p class="text-[11px] text-text-secondary font-mono truncate">
          ${cnt.role || 'Contacto'} • ${cnt.email || 'Sin email'} • ${cnt.phone || 'Sin tel'}
        </p>
      </div>

      <div class="flex items-center gap-1 shrink-0">
        <button type="button" class="btn-edit-contact px-2 py-0.5 text-[11px] font-semibold rounded border border-border bg-surface hover:bg-surface-subtle text-text-primary inline-flex items-center gap-1 shadow-sm" data-contact-id="${cnt.id}" title="Editar este contacto">
          ${getIcon('edit', 'w-3 h-3 text-text-secondary')}
          <span>Editar</span>
        </button>

        ${!cnt.is_primary ? `
          <button type="button" class="btn-make-primary text-[10px] font-semibold text-primary hover:underline px-1.5 py-0.5" data-client-id="${client.id}" data-contact-id="${cnt.id}" title="Marcar como contacto principal">
            Hacer Principal
          </button>
        ` : ''}

        <button type="button" class="btn-toggle-contact-dnc text-[10px] font-semibold ${cnt.do_not_contact ? 'text-emerald-600' : 'text-red-600'} hover:underline px-1.5 py-0.5" data-contact-id="${cnt.id}" data-dnc="${cnt.do_not_contact}">
          ${cnt.do_not_contact ? 'Habilitar' : 'No contactar'}
        </button>

        <button type="button" class="btn-delete-contact p-1 text-text-secondary hover:text-red-600 hover:bg-red-50 rounded" data-contact-id="${cnt.id}" data-contact-name="${cnt.name.replace(/"/g, '&quot;')}" title="Eliminar este contacto">
          ${getIcon('trash', 'w-3.5 h-3.5')}
        </button>
      </div>
    </div>
  `).join('');

  // Edit contact handler
  container.querySelectorAll('.btn-edit-contact').forEach(btn => {
    btn.onclick = () => {
      const contactId = Number(btn.dataset.contactId);
      const cnt = (client.contacts || []).find(c => c.id === contactId);
      if (!cnt) return;

      const form = document.getElementById('form-inline-add-contact');
      if (!form) return;

      document.getElementById('cnt-id').value = cnt.id;
      document.getElementById('cnt-name').value = cnt.name || '';
      document.getElementById('cnt-role').value = cnt.role || '';
      document.getElementById('cnt-email').value = cnt.email || '';
      document.getElementById('cnt-phone').value = cnt.phone || '';
      document.getElementById('cnt-form-title').textContent = `Editar Contacto: ${cnt.name}`;
      document.getElementById('cnt-submit-text').textContent = 'Actualizar Contacto';

      form.classList.remove('hidden');
      form.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      document.getElementById('cnt-name')?.focus();
    };
  });

  // Delete contact handler
  container.querySelectorAll('.btn-delete-contact').forEach(btn => {
    btn.onclick = async () => {
      const contactId = Number(btn.dataset.contactId);
      const contactName = btn.dataset.contactName || 'este contacto';
      if (!confirm(`¿Estás seguro de eliminar al contacto "${contactName}"?`)) return;

      try {
        await DataService.deleteContact(contactId);
        showToast('Contacto eliminado correctamente.');
        const updatedClients = await DataService.getClients();
        cachedClients = updatedClients;
        const currentClient = updatedClients.find(c => String(c.id) === String(client.id));
        if (currentClient) renderClientContactsList(currentClient);
      } catch (err) {
        showToast(`Error al eliminar contacto: ${err.message}`, 'error');
      }
    };
  });

  // Event handlers for making primary contact
  container.querySelectorAll('.btn-make-primary').forEach(btn => {
    btn.onclick = async () => {
      await DataService.setPrimaryContact(Number(btn.dataset.clientId), Number(btn.dataset.contactId));
      showToast('Contacto marcado como principal.');
      const updatedClients = await DataService.getClients();
      cachedClients = updatedClients;
      const currentClient = updatedClients.find(c => String(c.id) === String(client.id));
      if (currentClient) renderClientContactsList(currentClient);
    };
  });

  // Event handlers for toggling do_not_contact
  container.querySelectorAll('.btn-toggle-contact-dnc').forEach(btn => {
    btn.onclick = async () => {
      const currentDnc = btn.dataset.dnc === 'true';
      await DataService.updateContact(Number(btn.dataset.contactId), { do_not_contact: !currentDnc });
      showToast(!currentDnc ? 'Contacto excluido de envíos (No contactar).' : 'Contacto habilitado para envíos.');
      const updatedClients = await DataService.getClients();
      cachedClients = updatedClients;
      const currentClient = updatedClients.find(c => String(c.id) === String(client.id));
      if (currentClient) renderClientContactsList(currentClient);
    };
  });
}

// -------------------------------------------------------------
// RENDER CLIENT EMAIL HISTORY IN MODAL
// -------------------------------------------------------------
function renderClientEmailHistory(client, senderFilter = 'all') {
  const container = document.getElementById('client-email-history-list');
  if (!container) return;

  // Filter logs for this client
  let logs = cachedLogs.filter(l => String(l.client_id) === String(client.id));
  if (senderFilter !== 'all') {
    logs = logs.filter(l => String(l.account_id) === String(senderFilter));
  }

  // Populate sender filter dropdown
  const senderSelect = document.getElementById('filter-history-sender');
  if (senderSelect) {
    const clientLogs = cachedLogs.filter(l => String(l.client_id) === String(client.id));
    const usedAccountIds = new Set(clientLogs.map(l => l.account_id).filter(Boolean));
    senderSelect.innerHTML = `<option value="all" ${senderFilter === 'all' ? 'selected' : ''}>Todos los remitentes (${clientLogs.length})</option>`;
    
    cachedAccounts.forEach(acc => {
      const countForAcc = clientLogs.filter(l => l.account_id === acc.id).length;
      if (countForAcc > 0 || usedAccountIds.size === 0) {
        const opt = document.createElement('option');
        opt.value = acc.id;
        opt.textContent = `${acc.name} (${countForAcc})`;
        if (String(senderFilter) === String(acc.id)) opt.selected = true;
        senderSelect.appendChild(opt);
      }
    });

    senderSelect.onchange = (e) => {
      renderClientEmailHistory(client, e.target.value);
    };
  }

  if (logs.length === 0) {
    container.innerHTML = `<p class="text-text-secondary italic p-3 bg-surface-subtle rounded text-center text-xs">No hay registros de comunicaciones ${senderFilter !== 'all' ? 'con este remitente' : 'para este cliente aún'}.</p>`;
    return;
  }

  container.innerHTML = logs.map(l => {
    const hasReplied = !!l.replied_at;
    const authorName = l.account?.name || 'Equipo Alerce';
    const authorEmail = l.account?.email_address ? ` <${l.account.email_address}>` : '';
    const contactName = l.contact?.name || 'Contacto';
    const contactEmail = l.contact?.email ? ` (${l.contact.email})` : '';
    const sentDate = new Date(l.sent_at).toLocaleDateString('es-AR') + ' ' + new Date(l.sent_at).toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' });
    const replyDate = l.replied_at ? new Date(l.replied_at).toLocaleDateString('es-AR') + ' ' + new Date(l.replied_at).toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' }) : '';

    // Detectar si el registro corresponde a una notificación interna al operador/dirección
    const isInternalAlert = !l.contact_id || 
      l.reply_channel === 'internal_notification' || 
      (l.subject || '').startsWith('⚡ Nuevo Lead') || 
      (l.subject || '').startsWith('⚡ Nuevo Diagnóstico') ||
      (l.subject || '').includes('Nuevo Lead') ||
      (l.subject || '').includes('Nuevo Diagnóstico');

    const cardBgClass = isInternalAlert 
      ? 'bg-purple-50/40 border-purple-200/90 hover:border-purple-300' 
      : 'bg-surface border-border hover:border-border/90';

    const typeBadge = isInternalAlert
      ? `<span class="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-mono font-semibold uppercase tracking-wider bg-purple-100 text-purple-800 border border-purple-200">
           ${getIcon('bell', 'w-2.5 h-2.5 text-purple-600')} Notificación Interna a Dirección
         </span>`
      : `<span class="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-mono font-semibold uppercase tracking-wider bg-emerald-50 text-emerald-800 border border-emerald-200">
           ${getIcon('mail', 'w-2.5 h-2.5 text-emerald-600')} Email Enviado al Lead
         </span>`;

    const recipientDisplay = isInternalAlert
      ? `<p class="text-[11px] text-purple-900 font-medium">
           Para: <span class="font-bold text-purple-950">Operador / Dirección</span> <span class="font-mono text-[10px] text-purple-700 font-normal">(Aviso interno de nuevo lead)</span>
         </p>`
      : `<p class="text-[11px] text-text-secondary">
           Para: <span class="font-medium text-text-primary">${escapeHtml(contactName)}</span> <span class="font-mono text-[10px]">${escapeHtml(contactEmail)}</span>
         </p>`;

    const subjectBoxClass = isInternalAlert
      ? 'p-2 rounded bg-purple-100/40 border border-purple-200/60 flex items-center justify-between text-[11px]'
      : 'p-2 rounded bg-surface-subtle border border-border/60 flex items-center justify-between text-[11px]';

    return `
      <div class="p-3 rounded-md ${cardBgClass} border space-y-2 text-xs shadow-xs transition-all">
        <!-- Outbound Header with Sender / Recipient / Badges -->
        <div class="flex items-start justify-between gap-2">
          <div class="space-y-1 min-w-0">
            <div class="flex items-center gap-2 flex-wrap">
              ${typeBadge}
              <span class="text-[10px] text-text-secondary font-mono">${authorName}${authorEmail}</span>
            </div>
            ${recipientDisplay}
          </div>

          <div class="flex items-center gap-1.5 shrink-0">
            <span class="text-[10px] font-mono text-text-secondary whitespace-nowrap">${sentDate}</span>
            <span class="px-1.5 py-0.5 rounded text-[9px] font-mono font-semibold uppercase border ${
              l.status === 'replied' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' :
              l.status === 'clicked' ? 'bg-blue-50 text-blue-700 border-blue-200' :
              l.status === 'opened' ? 'bg-zinc-100 text-zinc-700 border-zinc-200' :
              'bg-surface-subtle text-text-secondary border-border'
            }">
              ${l.status}
            </span>
          </div>
        </div>

        <!-- Subject & Engagement Metrics -->
        <div class="${subjectBoxClass}">
          <p class="font-medium text-text-primary truncate mr-2" title="${escapeHtml(l.subject)}">
            "${escapeHtml(l.subject)}"
          </p>
          <div class="flex items-center gap-2 font-mono text-[10px] text-text-secondary shrink-0">
            <span title="Aperturas">${getIcon('eye', 'w-3 h-3 text-text-secondary inline')} ${l.open_count || (l.opened_at ? 1 : 0)}</span>
            ${(l.clicks?.length || 0) > 0 ? `<span class="text-blue-600 font-bold" title="Clicks">${getIcon('mousePointer', 'w-3 h-3 inline')} ${l.clicks.length}</span>` : ''}
          </div>
        </div>

        <!-- Inbound Reply Card (If Replied) -->
        ${hasReplied ? `
          <div class="p-2.5 rounded-md bg-emerald-50/80 border border-emerald-200/90 text-[11px] space-y-1.5">
            <div class="flex items-center justify-between">
              <span class="font-bold text-emerald-800 flex items-center gap-1.5">
                ${getIcon('messageSquare', 'w-3.5 h-3.5 text-emerald-600')}
                <span>Respuesta del Lead (${l.reply_channel || 'email'})</span>
              </span>
              <span class="text-[10px] font-mono text-emerald-700 font-semibold">${replyDate}</span>
            </div>
            <p class="text-emerald-950 leading-relaxed text-[11px] italic">
              ${l.reply_notes ? `"${escapeHtml(l.reply_notes)}"` : 'Respuesta detectada en buzón de entrada. Secuencia detenida y contacto registrado.'}
            </p>
            <div class="pt-1 flex items-center justify-between text-[10px]">
              <a href="https://mail.google.com/mail/u/0/#search/${encodeURIComponent(l.subject)}" target="_blank" class="text-emerald-700 hover:text-emerald-900 underline flex items-center gap-1 font-medium">
                ${getIcon('externalLink', 'w-2.5 h-2.5')}
                <span>Abrir en Gmail</span>
              </a>
              <button type="button" class="btn-edit-reply-notes font-semibold text-emerald-700 hover:text-emerald-900 underline" data-log-id="${l.id}">
                ${l.reply_notes ? 'Editar notas de respuesta' : '+ Agregar notas de respuesta'}
              </button>
            </div>
          </div>
        ` : ''}
      </div>
    `;
  }).join('');

  // Bind edit reply notes buttons
  container.querySelectorAll('.btn-edit-reply-notes').forEach(btn => {
    btn.onclick = async () => {
      const logId = btn.dataset.logId;
      const log = cachedLogs.find(l => String(l.id) === String(logId));
      if (!log) return;
      const currentNotes = log.reply_notes || '';
      const newNotes = prompt('Escribe un resumen o notas de lo que respondió el cliente:', currentNotes);
      if (newNotes !== null) {
        try {
          await DataService.updateEmailLog(log.id, { reply_notes: newNotes });
          log.reply_notes = newNotes;
          showToast('Notas de la comunicación actualizadas.');
          renderClientEmailHistory(client, senderFilter);
        } catch (err) {
          showToast(`Error al guardar notas: ${err.message}`, 'error');
        }
      }
    };
  });
}

// -------------------------------------------------------------
// DIRECT REACTIVATION MODAL (No Campaign Needed)
// -------------------------------------------------------------
function openDirectReactivateModal(clientId) {
  const container = document.getElementById('pipeline-extra-modals-container');
  if (!container) return;

  const client = cachedClients.find(c => String(c.id) === String(clientId));
  if (!client) return;

  const contacts = (client.contacts || []).filter(c => !c.do_not_contact && c.email);
  if (contacts.length === 0) {
    alert('Este cliente no tiene ningún contacto con email habilitado para enviar comunicaciones.');
    return;
  }

  container.innerHTML = `
    <div class="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div class="bg-surface rounded-lg border border-border shadow-2xl max-w-lg w-full p-5 space-y-4 animate-fade-in text-xs">
        <div class="flex items-center justify-between border-b border-border pb-3">
          <div>
            <h3 class="font-bold text-sm text-text-primary">Reactivar Cliente Directamente</h3>
            <p class="text-[11px] text-text-secondary">Envío individual ágil a ${client.name} sin crear una campaña</p>
          </div>
          <button id="btn-close-reactivate" class="text-text-secondary hover:text-text-primary p-1">✕</button>
        </div>

        <form id="form-direct-reactivate" class="space-y-3">
          <div>
            <label class="block font-semibold text-text-primary mb-1">Destinatario (Contacto)</label>
            <select id="dr-contact" class="w-full p-2 bg-surface-subtle border border-border rounded font-medium outline-none">
              ${contacts.map(cnt => `<option value="${cnt.id}" ${cnt.is_primary ? 'selected' : ''}>${cnt.name} (${cnt.email}) - ${cnt.role || 'Contacto'}</option>`).join('')}
            </select>
          </div>

          <div>
            <label class="block font-semibold text-text-primary mb-1">Cuenta de Envío de Google</label>
            <select id="dr-account" class="w-full p-2 bg-surface-subtle border border-border rounded font-medium outline-none">
              ${cachedAccounts.map(a => `<option value="${a.id}">${a.name} (${a.email_address})</option>`).join('')}
            </select>
          </div>

          <div>
            <label class="block font-semibold text-text-primary mb-1">Plantilla de Reactivación</label>
            <select id="dr-template" class="w-full p-2 bg-surface-subtle border border-border rounded font-medium outline-none">
              ${cachedTemplates.map(t => `<option value="${t.id}">${t.name}</option>`).join('')}
            </select>
          </div>

          <div class="p-3 bg-surface-subtle rounded border border-border/70 space-y-1 text-[11px] text-text-secondary">
            <p class="font-semibold text-text-primary">Lógica de Reactivación Directa:</p>
            <p>• Actualiza automáticamente la fecha de último contacto en el CRM.</p>
            <p>• Incluye tracking de apertura y de respuesta.</p>
            <p>• Registra la actividad comercial en la ficha del cliente.</p>
          </div>

          <div class="flex justify-end gap-2 pt-3 border-t border-border">
            <button type="button" id="btn-cancel-reactivate" class="px-3.5 py-1.5 border border-border rounded bg-surface text-text-secondary">Cancelar</button>
            <button type="submit" class="px-4 py-1.5 bg-primary text-white rounded font-semibold hover:bg-primary-hover shadow-sm">
              Enviar Email de Reactivación
            </button>
          </div>
        </form>
      </div>
    </div>
  `;

  const close = () => { container.innerHTML = ''; };
  document.getElementById('btn-close-reactivate')?.addEventListener('click', close);
  document.getElementById('btn-cancel-reactivate')?.addEventListener('click', close);

  const form = document.getElementById('form-direct-reactivate');
  form.onsubmit = async (e) => {
    e.preventDefault();
    const contactId = Number(document.getElementById('dr-contact').value);
    const accountId = Number(document.getElementById('dr-account').value);
    const templateId = Number(document.getElementById('dr-template').value);

    try {
      await DataService.sendDirectEmail({
        clientId: client.id,
        contactId,
        accountId,
        templateId
      });
      showToast('¡Email de reactivación enviado con éxito!');
      close();
      window.dispatchEvent(new HashChangeEvent('hashchange'));
    } catch (err) {
      alert(`Error enviando email: ${err.message}`);
    }
  };
}

// -------------------------------------------------------------
// MANUAL REPLY REGISTRATION MODAL (Cancels Sequences)
// -------------------------------------------------------------
function openRecordReplyModal(clientId) {
  const container = document.getElementById('pipeline-extra-modals-container');
  if (!container) return;

  const client = cachedClients.find(c => String(c.id) === String(clientId));
  if (!client) return;

  const contacts = client.contacts || [];

  container.innerHTML = `
    <div class="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div class="bg-surface rounded-lg border border-border shadow-2xl max-w-md w-full p-5 space-y-4 animate-fade-in text-xs">
        <div class="flex items-center justify-between border-b border-border pb-3">
          <div>
            <h3 class="font-bold text-sm text-text-primary">Registrar Respuesta de Cliente</h3>
            <p class="text-[11px] text-text-secondary">Detiene secuencias automáticas y actualiza el estado comercial</p>
          </div>
          <button id="btn-close-reply-modal" class="text-text-secondary hover:text-text-primary p-1">✕</button>
        </div>

        <form id="form-record-reply" class="space-y-3">
          <div>
            <label class="block font-semibold text-text-primary mb-1">Contacto que respondió</label>
            <select id="rr-contact" class="w-full p-2 bg-surface-subtle border border-border rounded outline-none font-medium">
              ${contacts.map(cnt => `<option value="${cnt.id}" ${cnt.is_primary ? 'selected' : ''}>${cnt.name} (${cnt.email || 'Sin email'})</option>`).join('')}
            </select>
          </div>

          <div>
            <label class="block font-semibold text-text-primary mb-1">Canal de Respuesta</label>
            <select id="rr-channel" class="w-full p-2 bg-surface-subtle border border-border rounded outline-none font-medium">
              <option value="whatsapp">WhatsApp / Mensajería</option>
              <option value="email">Email directo</option>
              <option value="phone">Llamada telefónica</option>
              <option value="meeting">Reunión / Videollamada</option>
            </select>
          </div>

          <div>
            <label class="block font-semibold text-text-primary mb-1">Avanzar Etapa Comercial en CRM</label>
            <select id="rr-stage" class="w-full p-2 bg-surface-subtle border border-border rounded outline-none font-medium">
              <option value="">-- Mantener etapa actual (${client.phase || 'Cliente potencial'}) --</option>
              <option value="Propuesta en curso">Avanzar a: Propuesta en curso</option>
              <option value="Cerrado">Avanzar a: Ganado / Activo</option>
              <option value="Descartado">Mover a: Descartado</option>
            </select>
          </div>

          <div>
            <label class="block font-semibold text-text-primary mb-1">Notas / Resumen de la respuesta</label>
            <textarea id="rr-notes" rows="2" placeholder="El cliente mostró interés en automatizar su facturación..." class="w-full p-2 bg-surface-subtle border border-border rounded outline-none"></textarea>
          </div>

          <div class="p-2.5 bg-emerald-50 rounded border border-emerald-200 text-emerald-800 text-[11px]">
            ✓ Al guardar, se detendrán de inmediato todos los correos programados pendientes de cualquier campaña hacia este contacto.
          </div>

          <div class="flex justify-end gap-2 pt-3 border-t border-border">
            <button type="button" id="btn-cancel-reply" class="px-3.5 py-1.5 border border-border rounded bg-surface text-text-secondary">Cancelar</button>
            <button type="submit" class="px-4 py-1.5 bg-emerald-600 text-white rounded font-semibold hover:bg-emerald-700 shadow-sm">
              Registrar Respuesta y Detener Secuencia
            </button>
          </div>
        </form>
      </div>
    </div>
  `;

  const close = () => { container.innerHTML = ''; };
  document.getElementById('btn-close-reply-modal')?.addEventListener('click', close);
  document.getElementById('btn-cancel-reply')?.addEventListener('click', close);

  const form = document.getElementById('form-record-reply');
  form.onsubmit = async (e) => {
    e.preventDefault();
    const contactId = Number(document.getElementById('rr-contact').value);
    const channel = document.getElementById('rr-channel').value;
    const stage = document.getElementById('rr-stage').value || null;
    const notes = document.getElementById('rr-notes').value;

    try {
      await DataService.logManualReply({
        clientId: client.id,
        contactId,
        channel,
        notes,
        newStage: stage
      });
      showToast('¡Respuesta registrada! Secuencia automática detenida.');
      close();
      window.dispatchEvent(new HashChangeEvent('hashchange'));
    } catch (err) {
      alert(`Error registrando respuesta: ${err.message}`);
    }
  };
}
